// Scroll-snap carousels: keeps the "2/8" counter in step with the visible slide and wires the
// previous/next buttons (shown from 990px). Swiping is plain native scrolling; no autoplay.
// Also slides the sideways rows below to a control that Tab reaches half hidden.
const VISIBLE_ENOUGH = 0.6;
// A focus ring reaches this far past its control (2px wide, 2px off).
const FOCUS_RING_PX = 4;
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
// Carousels wired so far, each with its counter's observer (or null), so none is ever wired twice.
const wired = new WeakMap();

function watchCounter(track, counter, slides) {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) counter.textContent = `${slides.indexOf(entry.target) + 1}/${slides.length}`;
      });
    },
    { root: track, threshold: VISIBLE_ENOUGH }
  );
  slides.forEach((slide) => observer.observe(slide));
  return observer;
}

function setUp(carousel) {
  const track = carousel.querySelector('[data-carousel-track]');
  if (!track || wired.has(carousel)) return;
  const counter = carousel.querySelector('[data-carousel-counter]');
  const slides = [...track.children];

  // One view of whole slides per press (one banner, or four to six cards), measured on each press
  // because slide widths change with the screen. It stops on a slide's own edge: desktop Safari does
  // not re-snap a smooth scroll, so a step in between would stop halfway across a slide.
  // Fractional boxes: whole-pixel offsets undercount a view of 222.5px cards by one.
  const go = (direction) => {
    const first = slides[0].getBoundingClientRect();
    const step = slides.length > 1 ? slides[1].getBoundingClientRect().left - first.left : track.clientWidth;
    const perView = 1 + Math.floor((track.clientWidth - first.width + 1) / step);
    const index = Math.round(track.scrollLeft / step) + direction * perView;
    const target = slides[Math.min(Math.max(index, 0), slides.length - 1)];
    track.scrollTo({ left: target.offsetLeft - slides[0].offsetLeft, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };
  const prev = carousel.querySelector('[data-carousel-prev]');
  const next = carousel.querySelector('[data-carousel-next]');
  prev?.addEventListener('click', () => go(-1));
  next?.addEventListener('click', () => go(1));

  // At either end the arrow that has nowhere to go is marked disabled; aria-disabled keeps keyboard
  // focus on it. Checked again whenever the row scrolls or changes size.
  const markEnds = () => {
    prev?.setAttribute('aria-disabled', track.scrollLeft < 1);
    next?.setAttribute('aria-disabled', track.scrollLeft > track.scrollWidth - track.clientWidth - 1);
  };
  track.addEventListener('scroll', markEnds, { passive: true });
  new ResizeObserver(markEnds).observe(track);

  wired.set(carousel, counter && slides.length > 1 ? watchCounter(track, counter, slides) : null);
}

const setUpAll = (root) => root.querySelectorAll('[data-carousel]').forEach(setUp);

setUpAll(document);
// Theme editor: an edited section comes back as new markup, but this module runs only once per page.
document.addEventListener('shopify:section:load', (event) => setUpAll(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-carousel]').forEach((carousel) => wired.get(carousel)?.disconnect());
});

// Tab to a control that a sideways row half hides (the filter chips, similar products): the browser
// leaves the row where it is, so slide it until the control and its focus ring show whole. Only the
// row moves, never the page (scrollIntoView would also scroll the page under the stuck Filter row).
// Taps never move it (no :focus-visible). A snap row then settles on a snap point that keeps it whole.
document.addEventListener('focusin', (event) => {
  const row = event.target.closest('.facets__bar, .product-similar__track');
  if (!row || !event.target.matches(':focus-visible')) return;
  const view = row.getBoundingClientRect();
  const box = (event.target.closest('.chip') ?? event.target).getBoundingClientRect();
  if (box.right + FOCUS_RING_PX > view.right) row.scrollLeft += box.right + FOCUS_RING_PX - view.right;
  else if (box.left - FOCUS_RING_PX < view.left) row.scrollLeft -= view.left - box.left + FOCUS_RING_PX;
});
